import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CoachButton, ErrorNote, Eyebrow, GoldSeal, SageCheck } from '../components/primitives';
import { StepFooter, StepRail } from '../components/step-rail';
import { apiErrorPayload, coaching } from '../lib/api';
import { ONBOARDING_STEP_LABELS } from '../lib/onboarding-steps';
import { qk, useFeature } from '../lib/queries';

/** The six onboarding steps, all behind us, plus the one being written now. */
const RAIL_STEPS = [...ONBOARDING_STEP_LABELS, 'Your blueprint'] as const;

const LINES = [
  'Analyzing your goals',
  'Understanding your success profile',
  'Creating your daily routine',
  'Preparing your first action plan',
  'Personalizing your coaching',
];

/** SC-12: writes the first plan while the member watches it come together. */
export function BlueprintPage() {
  const navigate = useNavigate();
  const programs = useFeature('programs');
  const qc = useQueryClient();
  const [shown, setShown] = useState(1);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const started = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => setShown((n) => Math.min(n + 1, LINES.length)), 850);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    coaching
      .completeOnboarding()
      .then(async () => {
        await qc.invalidateQueries({ queryKey: qk.root });
        setDone(true);
      })
      .catch((err) => {
        if (apiErrorPayload(err)?.error === 'primary_goal_required')
          navigate('/onboarding/goal', { replace: true });
        else setError(err);
      });
  }, [navigate, qc]);

  if (error) return <ErrorNote error={error} retry={() => window.location.reload()} />;
  const ready = done && shown >= LINES.length;

  return (
    <div className="lg:grid lg:min-h-screen lg:grid-cols-[360px_minmax(0,1fr)]">
      <StepRail
        title="Your Success Plan"
        steps={RAIL_STEPS}
        index={ONBOARDING_STEP_LABELS.length}
        note={
          ready
            ? 'Your plan is written. From here it is one day at a time.'
            : 'Writing your plan from the answers you gave.'
        }
      />
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pb-10 pt-8 lg:mx-0 lg:max-w-none lg:px-16 lg:pb-14 lg:pt-[88px] xl:px-[120px]">
        <div className="flex flex-1 flex-col lg:max-w-[640px]">
          <p className="font-display text-lg font-semibold text-ink lg:hidden">Miracles Protocol</p>
          <div className="flex flex-1 flex-col justify-center">
            {!ready ? (
              <ul className="space-y-4" aria-live="polite">
                {LINES.slice(0, shown).map((line, i) => (
                  <li key={line} className="flex items-center gap-3 text-[0.98rem] text-ink">
                    {i < shown - 1 || done ? (
                      <SageCheck size={18} />
                    ) : (
                      <span className="h-[18px] w-[18px] animate-pulse rounded-full border border-[rgba(16,32,26,0.35)]" />
                    )}
                    {line}
                  </li>
                ))}
              </ul>
            ) : (
              <div>
                <GoldSeal size={44} />
                <Eyebrow className="mb-3 mt-6">Your Success Blueprint is ready</Eyebrow>
                <h1 className="font-display text-[2.2rem] font-semibold leading-[1.08] text-ink lg:text-[2.5rem] lg:leading-[1.1]">
                  Everything starts with one clear action.
                </h1>
              </div>
            )}
          </div>
          {ready && (
            <StepFooter progress={1}>
              {programs !== 'off' && (
                <CoachButton
                  kind="gold"
                  block
                  onClick={() => navigate('/programs/seven-day-start', { replace: true })}
                >
                  Start the 7-day program
                </CoachButton>
              )}
              <CoachButton
                kind="outline"
                block
                className={programs !== 'off' ? 'mt-3 lg:mt-0' : undefined}
                onClick={() => navigate('/home', { replace: true })}
              >
                Open my dashboard
              </CoachButton>
            </StepFooter>
          )}
        </div>
      </div>
    </div>
  );
}
